/**
 * ContentCard — Farmly's canonical visual card.
 * Locked hierarchy: place > experience > producer > event.
 * No box-shadow, no border, no image zoom.
 */

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

type CardVariant = 'place' | 'experience' | 'producer' | 'event';

interface ContentCardProps {
  variant: CardVariant;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  href: string;
  /** Optional date string for event variant */
  date?: string | null;
  className?: string;
}

const ASPECT_RATIOS: Record<CardVariant, string> = {
  place: 'aspect-[4/3]',
  experience: 'aspect-[3/2]',
  producer: 'aspect-[3/2]',
  event: 'aspect-[3/2]',
};

const IMAGE_HEIGHTS: Record<CardVariant, string> = {
  place: 'h-[75%]',       // ~75% image dominance
  experience: 'h-auto',   // 55-60% via aspect ratio
  producer: 'h-auto',     // smaller, ~45-50%
  event: 'h-auto',
};

export function ContentCard({
  variant,
  title,
  subtitle,
  description,
  imageUrl,
  href,
  date,
  className,
}: ContentCardProps) {
  return (
    <Link
      to={href}
      className={cn(
        // Base: subtle plate, rounded, generous padding, no border/shadow
        'group block rounded-xl overflow-hidden transition-all duration-[160ms] ease-out',
        'bg-foreground/[0.02]',
        // Hover: lift
        'hover:-translate-y-0.5',
        'hover:bg-foreground/[0.04]',
        // Active (mobile): scale down
        'active:scale-[0.985]',
        className,
      )}
    >
      {/* Image */}
      {imageUrl && (
        <div
          className={cn(
            'w-full overflow-hidden bg-muted',
            ASPECT_RATIOS[variant],
            variant === 'experience' && 'saturate-[0.85]',
          )}
        >
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <div
        className={cn(
          'px-4 pt-3 pb-4',
          variant === 'place' && 'px-4 pt-3 pb-4',
          variant === 'producer' && 'px-4 pt-3 pb-4',
        )}
      >
        {/* Date badge for events */}
        {variant === 'event' && date && (
          <span className="text-xs text-muted-foreground/70 tracking-wide mb-1 block">
            {date}
          </span>
        )}

        <h3
          className={cn(
            'font-display font-medium text-foreground leading-snug',
            variant === 'place' && 'text-base sm:text-lg',
            variant === 'experience' && 'text-base',
            variant === 'producer' && 'text-[15px]',
            variant === 'event' && 'text-[15px]',
          )}
        >
          {title}
        </h3>

        {subtitle && (
          <p className="mt-1 text-sm text-muted-foreground/70 line-clamp-1">
            {subtitle}
          </p>
        )}

        {description && (
          <p
            className={cn(
              'mt-1.5 text-sm text-muted-foreground/60 leading-relaxed',
              variant === 'producer' ? 'line-clamp-3' : 'line-clamp-2',
            )}
          >
            {description}
          </p>
        )}
      </div>
    </Link>
  );
}
